import { useCallback, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import type { GridApi } from 'ag-grid-community';
import { format } from 'date-fns';
import { LoaderCircleIcon } from 'lucide-react';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { type FieldErrors, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useGetBreedsQuery } from '@/app/services/lookups';
import { AspirationDate } from '@/pages/bulk-donor-scheduling/components/aspiration-date';
import { AspirationDropdown } from '@/pages/bulk-donor-scheduling/components/aspiration-dropdown';
import {
  type DonorMismatchData,
  DonorMismatchDialog,
} from '@/pages/bulk-donor-scheduling/components/donor-mismatch-dialog';
import { FertilizationDropdown } from '@/pages/bulk-donor-scheduling/components/fertilization-dropdown';
import {
  type BulkDonorScheduleValidationResponse,
  type UpsertBulkDonorScheduleRequest,
  useUpsertBulkDonorSchedules,
  useValidateBulkDonorSchedules,
  type ValidateBulkDonorScheduleRequest,
  ValidationMessageLevel,
} from '@/services/bulk-donor-schedule';

import { BulkDonorScheduleGrid } from './components/bulk-donor-schedule-grid';
import { UnsavedChangesGuard } from './components/unsaved-changes-guard';
import {
  type Messages,
  useBulkDonorScheduleStore,
} from './hooks/use-bulk-donor-schedule-store';
import { useColumns } from './hooks/use-columns';
import { useNotifications } from './hooks/use-notifications';
import { parseExcelFile } from './utils/excel-parser';
import {
  type BulkDonorScheduleFieldsInput,
  BulkDonorScheduleFieldsSchema,
  type BulkDonorScheduleInput,
  BulkDonorScheduleRowsSchema,
} from './utils/schema';

const normalizeBreedValue = (value: string) => value.trim().toLowerCase();

export function BulkDonorSchedulePage() {
  const { t } = useTranslation();

  const [state, dispatch] = useBulkDonorScheduleStore();
  const { data: bovineBreeds } = useGetBreedsQuery({});

  const { control, handleSubmit, reset, getValues } =
    useForm<BulkDonorScheduleFieldsInput>({
      resolver: zodResolver(BulkDonorScheduleFieldsSchema),
    });

  const [api, setApi] = useState<GridApi<BulkDonorScheduleInput>>();
  const [isGridEditing, setGridEditing] = useState(false);
  const [mismatchDialog, setMismatchDialog] = useState<{
    visible: boolean;
    rowIndex: number | null;
    donorName: string;
    mismatchData: DonorMismatchData | null;
  }>({
    visible: false,
    rowIndex: null,
    donorName: '',
    mismatchData: null,
  });

  const notifications = useNotifications();

  const validBovineBreedValues = useMemo(() => {
    const values = new Set<string>();
    for (const breed of bovineBreeds || []) {
      if (breed?.code) {
        values.add(normalizeBreedValue(breed.code));
      }
      if (breed?.name) {
        values.add(normalizeBreedValue(breed.name));
      }
    }
    return values;
  }, [bovineBreeds]);

  const fertilizationSiteId = useWatch({
    control,
    name: 'fertilizationSiteId',
  });

  const columns = useColumns({
    fertilizationSiteId,
  });

  const validateSchedule = useValidateBulkDonorSchedules();
  const upsertSchedulesMutation = useUpsertBulkDonorSchedules();

  const handleValidate = useCallback(
    async (fields: BulkDonorScheduleFieldsInput) => {
      if (!api) {
        return;
      }

      dispatch({ type: 'client_validating' });
      api.stopEditing();

      const rows = state.rows.map((row) => {
        if (!row || Object.values(row).filter((v) => !!v).length === 0) {
          return undefined;
        }
        return row;
      });

      const rowsResult = BulkDonorScheduleRowsSchema.safeParse(rows);

      const inputs = rowsResult.data?.filter(
        (row): row is BulkDonorScheduleInput => row !== undefined,
      );

      if (!rowsResult.success || !inputs?.length) {
        const errors: Record<string, string> = {};

        const colId = api
          .getColumns()
          ?.find((c) => c.getColDef().field)
          ?.getColDef().field;
        if (!inputs?.length && colId) {
          errors[`${0}.${colId}`] = 'At least one valid row is required';
        }

        if (rowsResult.error?.issues) {
          for (const issue of rowsResult.error.issues) {
            const rowIndex = issue.path[0] as string;
            const colId = issue.path[1] as string;
            errors[`${rowIndex}.${colId}`] = issue.message;
          }
        }

        dispatch({ type: 'client_validated', errors });
        notifications.validationErrors();
        return;
      }

      if (bovineBreeds?.length) {
        const breedErrors: Record<string, string> = {};
        rowsResult.data.forEach((row, rowIndex) => {
          if (!row) {
            return;
          }

          const donorBreed = row.donorBreed?.trim();
          if (
            donorBreed &&
            !validBovineBreedValues.has(normalizeBreedValue(donorBreed))
          ) {
            breedErrors[`${rowIndex}.donorBreed`] =
              'Breed must be a valid bovine breed code or name';
          }
        });

        if (Object.keys(breedErrors).length > 0) {
          dispatch({ type: 'client_validated', errors: breedErrors });
          notifications.validationErrors();
          return;
        }
      }

      dispatch({ type: 'client_validated', errors: undefined });

      try {
        const variables: ValidateBulkDonorScheduleRequest = {
          ...fields,
          inputs: rowsResult.data as BulkDonorScheduleInput[],
          aspirationDate: format(
            fields.aspirationDate,
            "yyyy-MM-dd'T'HH:mm:ss",
          ),
        };

        const result: BulkDonorScheduleValidationResponse =
          await validateSchedule.mutateAsync(variables);

        let messages: Messages | undefined = undefined;
        let errors: Record<string, string> | undefined = undefined;

        for (const validation of result.fields) {
          const field = validation.field;
          if (!messages) {
            messages = {};
          }
          messages[field] = {
            level: ValidationMessageLevel.MESSAGE,
            message: validation.message,
          };
        }

        let hasMismatch = false;
        for (const validation of result.inputs) {
          const rowIndex = validation.index.toString();
          const colId = validation.field;

          if (
            validation.correctedValue &&
            validation.level === ValidationMessageLevel.MESSAGE
          ) {
            const updatedRow = {
              ...state.rows[validation.index],
              [colId]: validation.correctedValue,
            };
            dispatch({
              type: 'set_row',
              rowIndex: validation.index,
              data: updatedRow,
            });
          }

          if (validation.level === ValidationMessageLevel.ERROR) {
            if (!errors) {
              errors = {};
            }
            errors[`${rowIndex}.${colId}`] = validation.message;
          } else {
            if (!messages) {
              messages = {};
            }
            messages[`${rowIndex}.${colId}`] = {
              level: validation.level,
              message: validation.message,
              suggestions: validation.suggestions || undefined,
            };

            if (
              validation.level === ValidationMessageLevel.WARNING &&
              validation.mismatchData &&
              !hasMismatch
            ) {
              hasMismatch = true;
              const row = state.rows[validation.index];
              setMismatchDialog({
                visible: true,
                rowIndex: validation.index,
                donorName: row?.donorNameToMatch || '',
                mismatchData: validation.mismatchData,
              });
            }
          }
        }

        if (errors) {
          notifications.validationErrors();
        } else if (!hasMismatch) {
          notifications.validationSuccessful();
        }

        dispatch({ type: 'server_validated', errors, messages });
      } catch (error) {
        notifications.validationServerError(error);
        dispatch({ type: 'server_error' });
        console.error('Error while validating data', error);
      }
    },
    [
      api,
      dispatch,
      notifications,
      state.rows,
      validateSchedule,
      bovineBreeds,
      validBovineBreedValues,
    ],
  );

  const handleInvalidSubmit = useCallback(
    (_errors: FieldErrors<BulkDonorScheduleFieldsInput>) => {
      notifications.validationErrors();
    },
    [notifications],
  );

  const handleUpload = useCallback(async () => {
    if (!api) {
      return;
    }

    dispatch({ type: 'uploading' });

    try {
      const fields = getValues();
      const rows = BulkDonorScheduleRowsSchema.parse(
        state.rows.filter((row) => {
          return !!row && !!Object.values(row).filter((v) => !!v).length;
        }),
      ).filter((row): row is BulkDonorScheduleInput => row !== undefined);

      const variables: UpsertBulkDonorScheduleRequest = {
        ...fields,
        inputs: rows,
        aspirationDate: format(fields.aspirationDate, "yyyy-MM-dd'T'HH:mm:ss"),
      };

      await upsertSchedulesMutation.mutateAsync(variables);

      notifications.uploadSuccessful();
      dispatch({ type: 'success' });
    } catch (error) {
      notifications.uploadServerError(error);
      dispatch({ type: 'server_error' });
      console.error('Error while uploading data', error);
    }
  }, [
    api,
    dispatch,
    getValues,
    notifications,
    state.rows,
    upsertSchedulesMutation,
  ]);

  const handleRowChanged = useCallback(
    (rowIndex: number, data: BulkDonorScheduleInput) => {
      dispatch({ type: 'set_row', rowIndex, data });
    },
    [dispatch],
  );

  const handleUseExistingData = useCallback(() => {
    if (mismatchDialog.rowIndex === null || !mismatchDialog.mismatchData) {
      return;
    }

    const rowIndex = mismatchDialog.rowIndex;
    const currentRow = state.rows[rowIndex];
    if (!currentRow) {
      return;
    }

    const updatedRow: BulkDonorScheduleInput = {
      ...currentRow,
      donorRegistrationNumber:
        mismatchDialog.mismatchData.existingRegistrationNumber ||
        currentRow.donorRegistrationNumber,
      pedigreeName:
        mismatchDialog.mismatchData.existingPedigreeName ||
        currentRow.pedigreeName,
      birthDate:
        mismatchDialog.mismatchData.existingBirthDate || currentRow.birthDate,
    };

    dispatch({ type: 'set_row', rowIndex, data: updatedRow });

    setMismatchDialog({
      visible: false,
      rowIndex: null,
      donorName: '',
      mismatchData: null,
    });

    notifications.validationSuccessful();
  }, [mismatchDialog, state.rows, dispatch, notifications]);

  const handleCreateNewDonor = useCallback(() => {
    setMismatchDialog({
      visible: false,
      rowIndex: null,
      donorName: '',
      mismatchData: null,
    });

    notifications.validationSuccessful();
  }, [notifications]);

  const handleCancelMismatch = useCallback(() => {
    setMismatchDialog({
      visible: false,
      rowIndex: null,
      donorName: '',
      mismatchData: null,
    });
  }, []);

  const processFileUpload = useCallback(
    async (file: File) => {
      try {
        const data = await parseExcelFile(file);

        if (data.length === 0) {
          notifications.fileProcessingError(
            'No valid data found in Excel file',
          );
          return;
        }

        dispatch({ type: 'upload_from_excel', data });
        notifications.fileUploadSuccess(
          `Successfully loaded ${data.length} rows`,
        );
      } catch (error) {
        console.error('Error processing Excel file:', error);
        notifications.fileProcessingError(
          error instanceof Error
            ? error.message
            : 'Failed to process Excel file',
        );
      }
    },
    [dispatch, notifications],
  );

  const showFilePicker = useCallback(() => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.xlsx';
    fileInput.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        await processFileUpload(file);
      }
    };
    fileInput.click();
  }, [processFileUpload]);

  const handleUploadClick = useCallback(() => {
    const hasData = state.rows.some(
      (row) =>
        row &&
        Object.values(row).some((val) => val !== undefined && val !== ''),
    );

    if (!hasData) {
      showFilePicker();
      return;
    }

    confirmDialog({
      message: t('bulkDonorScheduling.uploadConfirmMessage'),
      header: t('bulkDonorScheduling.uploadConfirmHeader'),
      icon: 'pi pi-exclamation-triangle',
      accept: showFilePicker,
      acceptLabel: t('common.yes'),
      rejectLabel: t('common.no'),
    });
  }, [state.rows, showFilePicker, t]);

  return (
    <div className="h-[calc(100%-4rem)]">
      <ConfirmDialog />
      <UnsavedChangesGuard state={state} />

      <DonorMismatchDialog
        visible={mismatchDialog.visible}
        mismatchData={mismatchDialog.mismatchData}
        donorName={mismatchDialog.donorName}
        onUseExisting={handleUseExistingData}
        onCreateNew={handleCreateNewDonor}
        onCancel={handleCancelMismatch}
      />

      <form
        onSubmit={handleSubmit(handleValidate, handleInvalidSubmit)}
        className="mb-4 flex flex-col justify-between gap-4 lg:flex-row"
      >
        <div className="flex flex-col gap-2 md:flex-row">
          <AspirationDate
            className="flex-grow"
            control={control}
            disabled={state.readonly || state.loading}
            valueChanged={() => {
              dispatch({ type: 'idle' });
            }}
          />
          <AspirationDropdown
            className="flex-grow"
            control={control}
            disabled={state.readonly || state.loading}
            valueChanged={() => {
              dispatch({ type: 'idle' });
            }}
          />
          <FertilizationDropdown
            className="flex-grow"
            control={control}
            disabled={state.readonly || state.loading}
            valueChanged={() => {
              dispatch({ type: 'idle' });
            }}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {state.type === 'idle' && (
            <Button
              type="button"
              className="justify-center"
              disabled={state.loading || isGridEditing}
              onClick={handleUploadClick}
            >
              {t('bulkDonorScheduling.Upload')}
            </Button>
          )}
          {state.type === 'server_validated' ||
          state.type === 'uploading' ||
          state.type === 'success' ? (
            <Button
              severity="success"
              className="flex-grow justify-center"
              disabled={
                state.loading || state.type === 'success' || isGridEditing
              }
              onClick={handleUpload}
            >
              {state.loading ? (
                <>
                  <LoaderCircleIcon className="animate-spin" />
                  {t('bulkDonorScheduling.Uploading')}
                </>
              ) : (
                t('bulkDonorScheduling.Upload')
              )}
            </Button>
          ) : (
            <Button
              className="justify-center"
              disabled={state.loading || isGridEditing}
              type="submit"
            >
              {state.loading ? (
                <>
                  <LoaderCircleIcon className="animate-spin" />
                  {t('bulkDonorScheduling.Validating')}
                </>
              ) : (
                t('bulkDonorScheduling.Validate')
              )}
            </Button>
          )}

          <Button
            className="justify-center"
            disabled={
              state.loading || state.type === 'success' || isGridEditing
            }
            onClick={() => {
              dispatch({ type: 'add_rows', count: 10 });
            }}
          >
            {t('bulkDonorScheduling.Add_Rows')}
          </Button>

          <Button
            className="justify-center"
            size="small"
            severity="danger"
            disabled={state.loading || isGridEditing}
            onClick={() => {
              reset();
              dispatch({ type: 'reset' });
            }}
          >
            {t('bulkDonorScheduling.Reset')}
          </Button>
        </div>
      </form>

      <BulkDonorScheduleGrid
        columns={columns}
        onEditingChange={setGridEditing}
        onReady={setApi}
        onRowChanged={handleRowChanged}
        state={state}
      />
    </div>
  );
}
